import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";
import { Icon } from "./icon";

const button = cva(
  "inline-flex items-center justify-center gap-2 font-semibold tracking-wide transition-all duration-300 select-none cursor-pointer disabled:opacity-50 disabled:pointer-events-none whitespace-nowrap",
  {
    variants: {
      variant: {
        primary: "btn-primary-gradient rounded-full",
        glass: "btn-glass rounded-full text-[rgb(var(--fg))]",
        ghost: "rounded-full text-[rgb(var(--fg-muted))] hover:text-[rgb(var(--fg))] hover:bg-[rgb(var(--border)/0.08)]",
        outline:
          "rounded-full border border-[rgb(var(--accent)/0.5)] text-[rgb(var(--fg))] hover:bg-[rgb(var(--accent)/0.1)] hover:border-[rgb(var(--accent))]",
      },
      size: {
        sm: "h-9 px-4 text-sm",
        md: "h-11 px-6 text-[0.95rem]",
        lg: "h-13 px-8 text-base",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  iconRight?: string;
  iconLeft?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, iconRight, iconLeft, children, ...props }, ref) => (
    <button ref={ref} className={cn(button({ variant, size }), className)} {...props}>
      {iconLeft && <Icon name={iconLeft} className="h-4 w-4" />}
      {children}
      {iconRight && <Icon name={iconRight} className="h-4 w-4" />}
    </button>
  ),
);
Button.displayName = "Button";
